import { type FC, useState } from 'react';
import { AppWindow, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../../utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown/Dropdown';
import { Popover, PopoverContent, PopoverTrigger } from '../../atoms/Popover/Popover';
import { Button } from '../../atoms/Button/Button';
import { Heading } from '../../atoms/Typography/Typography';
import { Text } from '../../atoms/Typography/Typography';
import type { AppSwitcherApp } from './appSwitcherUtils';

export type { AgionAppId, AppSwitcherApp } from './appSwitcherUtils';
export {
  buildAgionAppUrl,
  createAgionAppSwitcherApps,
  getCurrentAgionAppId,
} from './appSwitcherUtils';

export interface AppSwitcherLabels {
  selectApp?: string;
  title?: string;
  description?: string;
  tooltip?: string;
}

const defaultLabels: Required<AppSwitcherLabels> = {
  selectApp: 'Select App',
  title: 'Applications',
  description: 'Switch between Agion applications',
  tooltip: 'Switch application',
};

export interface AppSwitcherProps {
  apps: AppSwitcherApp[];
  currentAppId?: string;
  variant?: 'header' | 'sidebar';
  collapsed?: boolean;
  labels?: AppSwitcherLabels;
  className?: string;
}

export const AppSwitcher: FC<AppSwitcherProps> = ({
  apps,
  currentAppId,
  variant = 'header',
  collapsed = false,
  labels: labelsProp,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const labels = { ...defaultLabels, ...labelsProp };
  const currentApp = apps.find((app) => app.id === currentAppId);
  const CurrentIcon = currentApp?.icon ?? AppWindow;

  if (apps.length === 0) {
    return null;
  }

  if (variant === 'sidebar') {
    // Collapsed: show a side popover so users can still switch apps from a minimized sidebar.
    if (collapsed) {
      return (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="app-switcher"
              title={labels.tooltip}
              aria-label={labels.tooltip}
              className={cn(
                'w-full flex items-center justify-center rounded-md px-0 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer',
                className
              )}
            >
              <CurrentIcon className="h-5 w-5 text-slate-600 dark:text-slate-400 flex-shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={8}
            className="w-56 p-1.5"
          >
            <div className="px-2 py-1 mb-1 border-b border-slate-200 dark:border-slate-700/50">
              <Text size="xs" weight="semibold" className="text-slate-500 dark:text-slate-400">
                {labels.title}
              </Text>
            </div>
            {apps.map((app) => {
              const Icon = app.icon ?? AppWindow;
              const isActive = app.id === currentAppId;
              return (
                <a
                  key={app.id}
                  href={app.href}
                  aria-current={isActive ? 'page' : undefined}
                  data-testid={`app-switcher-${app.id}`}
                  className={cn(
                    'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100'
                      : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/30'
                  )}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 truncate">{app.name}</span>
                  {isActive && <Check className="h-4 w-4 text-green-500 flex-shrink-0" />}
                </a>
              );
            })}
          </PopoverContent>
        </Popover>
      );
    }

    // Expanded: inline expandable list.
    return (
      <div className={cn('space-y-1', className)} data-testid="app-switcher">
        <div
          className="flex items-center gap-2.5 rounded-md px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={labels.tooltip}
          aria-expanded={isOpen}
        >
          <CurrentIcon className="h-5 w-5 text-slate-600 dark:text-slate-400 flex-shrink-0" />
          <Text as="span" className="flex-1 text-slate-700 dark:text-slate-300 truncate">
            {currentApp?.name || labels.selectApp}
          </Text>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          )}
        </div>

        {isOpen && (
          <div className="ml-7 mb-2 space-y-1">
            {apps.map((app) => {
              const Icon = app.icon ?? AppWindow;
              const isActive = app.id === currentAppId;

              return (
                <Button
                  key={app.id}
                  asChild
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'w-full flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors justify-start h-auto',
                    isActive
                      ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100'
                      : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/30'
                  )}
                >
                  <a
                    href={app.href}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => setIsOpen(false)}
                    data-testid={`app-switcher-${app.id}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1 text-left truncate">{app.name}</span>
                    {isActive && <Check className="h-4 w-4 text-green-500 flex-shrink-0" />}
                  </a>
                </Button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('h-10 px-3 flex items-center gap-2', className)}
          title={labels.tooltip}
          data-testid="app-switcher"
        >
          <CurrentIcon className="h-4 w-4" />
          <Text as="span" className="hidden sm:inline max-w-[150px] truncate">
            {currentApp?.name || labels.selectApp}
          </Text>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 p-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/50 rounded-lg shadow-lg"
      >
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700/50">
          <Heading level="h6" as="h3">
            {labels.title}
          </Heading>
          <Text size="xs" color="muted" className="dark:text-slate-400">
            {labels.description}
          </Text>
        </div>

        <div className="py-1 max-h-[300px] overflow-y-auto">
          {apps.map((app) => {
            const Icon = app.icon ?? AppWindow;
            const isActive = app.id === currentAppId;

            return (
              <DropdownMenuItem
                key={app.id}
                asChild
                className="flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/30 rounded-md transition-colors mx-1 cursor-pointer"
              >
                <a
                  href={app.href}
                  aria-current={isActive ? 'page' : undefined}
                  data-testid={`app-switcher-${app.id}`}
                >
                  <div className="w-8 h-8 rounded-md bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Text size="sm" weight="medium" className="truncate">
                      {app.name}
                    </Text>
                    {app.description && (
                      <Text size="xs" color="muted" className="truncate dark:text-slate-400">
                        {app.description}
                      </Text>
                    )}
                  </div>
                  {isActive && <Check className="h-4 w-4 text-green-500 flex-shrink-0" />}
                </a>
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
AppSwitcher.displayName = 'AppSwitcher';
